import { useFieldContext } from '@/lib/form-context';
import { Field, FieldContent, FieldError, FieldLabel } from '@/components/ui/field';
import { filterFieldErrors } from '../utils';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { type VariantProps } from 'class-variance-authority';
import { schemaFieldVariants } from '../constants';

export interface TypedOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

export interface TypedItem {
  app: string;
  handle: string;
}

interface TypedMultiInputProps extends VariantProps<typeof schemaFieldVariants> {
  label: string;
  options: TypedOption[];
  typePlaceholder?: string;
  valuePlaceholder?: string;
  readOnly?: boolean;
  loading?: boolean;
  disabled?: boolean;
}

export const TypedMultiInput = ({
  label,
  options,
  typePlaceholder = 'Type',
  valuePlaceholder = 'ID or handle',
  readOnly = false,
  loading = false,
  disabled = false,
  variant,
}: TypedMultiInputProps) => {
  const resolvedVariant = variant ?? 'inline';
  const field = useFieldContext<TypedItem[]>();
  const errors = field.state.meta.errors;
  const hasError = field.state.meta.isTouched && errors.length > 0;
  const value = field.state.value ?? [];

  const handleAdd = () => {
    const firstOption = options[0];
    field.handleChange([...value, { app: firstOption?.value ?? '', handle: '' }]);
  };

  const handleRemove = (index: number) => {
    field.handleChange(value.filter((_, i) => i !== index));
  };

  const handleTypeChange = (index: number, app: string) => {
    const next = [...value];
    next[index] = { ...next[index], app };
    field.handleChange(next);
  };

  const handleValueChange = (index: number, handle: string) => {
    const next = [...value];
    next[index] = { ...next[index], handle };
    field.handleChange(next);
  };

  return (
    <Field
      orientation={resolvedVariant === 'stacked' ? 'vertical' : 'subgrid'}
      data-invalid={hasError || undefined}
    >
      <FieldLabel>{label}</FieldLabel>
      <FieldContent>
        {loading ? (
          <Skeleton className="h-20 w-full" />
        ) : readOnly ? (
          <div className="space-y-2">
            {value.length === 0 ? (
              <p className="text-muted-foreground">—</p>
            ) : (
              value.map((item, i) => {
                const opt = options.find((o) => o.value === item.app);
                return (
                  <p key={i} className="text-sm flex items-center gap-2">
                    {opt?.icon && <span className="shrink-0 [&>svg]:size-4">{opt.icon}</span>}
                    {opt?.label ?? item.app}
                    {item.handle ? `: ${item.handle}` : ''}
                  </p>
                );
              })
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {value.map((item, index) => (
              <div key={index} className="relative rounded-md border bg-muted/30 px-3 pt-2 pb-3">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Remove"
                  disabled={disabled}
                  onClick={() => handleRemove(index)}
                  className="absolute top-1.5 right-1.5 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="size-3.5" />
                </Button>
                <div className="space-y-1.5 pr-8">
                  <Select
                    value={item.app}
                    disabled={disabled}
                    onValueChange={(v) => handleTypeChange(index, v ?? '')}
                  >
                    <SelectTrigger size="sm" className="w-full" disabled={disabled}>
                      <SelectValue placeholder={typePlaceholder}>
                        {(v: string) => {
                          const opt = options.find((o) => o.value === v);
                          return opt ? (
                            <span className="flex items-center gap-2">
                              {opt.icon && (
                                <span className="shrink-0 [&>svg]:size-4">{opt.icon}</span>
                              )}
                              {opt.label}
                            </span>
                          ) : null;
                        }}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {options.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <span className="flex items-center gap-2">
                            {opt.icon && (
                              <span className="shrink-0 [&>svg]:size-4">{opt.icon}</span>
                            )}
                            {opt.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={item.handle}
                    placeholder={valuePlaceholder}
                    disabled={disabled}
                    onChange={(e) => handleValueChange(index, e.target.value)}
                    onBlur={field.handleBlur}
                    className="w-full"
                  />
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={handleAdd}
              className="gap-1.5"
            >
              <Plus className="size-4" />
              Add
            </Button>
          </div>
        )}
        {hasError && <FieldError errors={filterFieldErrors(errors)} />}
      </FieldContent>
    </Field>
  );
};
